import React, { useMemo, useCallback, useState, useEffect } from "react";
import { DatePicker } from "@alm/flow-ds/components";
import { ElementComponentProps } from "elements/types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { State } from "data";
import {
	createGetElementData,
	ElementComponentData,
} from "data/form/elementSelectors";
import { DateElement } from "records/Element";
import { useStandardElement } from "components/Forms/Element";
import { DisplayOnlyValue } from "elements";
import { useDebounceSetDataReactive } from "hooks/useDebounceSetData";
import { isRTL } from "utils/getLanguageDir";

function DateReactive({
	fid,
	vid,
	id,
	index,
	rowIndex,
	uuid,
	hint,
	label,
	required,
	disabled,
	displayOnly,
}: ElementComponentProps<DateElement>) {
	const { t, i18n } = useTranslation();
	const getData = useMemo(createGetElementData, []);

	const selectorProps = useMemo(
		() => ({ fid, vid, id, index, rowIndex, uuid }),
		[fid, vid, id, index, rowIndex, uuid]
	);

	const data = useSelector((state: State) =>
		getData(state, selectorProps)
	) as ElementComponentData<string>;

	const { eid, setDataAndQueue, remove } = useStandardElement(selectorProps);

	// Setup callbacks for handling debouncing
	const { value, flushChanges, onChange } = useDebounceSetDataReactive(
		setDataAndQueue,
		data
	);

	const [isDateDisabled, setIsDateDisabled] = useState<boolean>(
		disabled || false
	);

	// Handle value changes
	const handleOnChange = useCallback(
		(value: string, calendarEvent: boolean) => {
			// Changes using the calendar are queued immediately
			// Direct text changes are debounced
			if (calendarEvent) {
				onChange(value);
			} else if (value === "") {
				setIsDateDisabled(true);
			} else {
				setDataAndQueue(data, { value, id });
			}
		},
		[data, id, onChange, setDataAndQueue]
	);

	useEffect(() => {
		if (isDateDisabled && !disabled) {
			remove();
			setIsDateDisabled(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDateDisabled]);

	// Display only
	if (displayOnly) {
		return <DisplayOnlyValue label={label} value={data.value || ""} />;
	}

	return (
		<>
			<DatePicker
				locale={i18n.language}
				onChange={handleOnChange}
				onBlur={flushChanges}
				monthLabel={t("elements.date.month")}
				yearLabel={t("elements.date.year")}
				nextMonthLabel={t("elements.date.nextMonth")}
				prevMonthLabel={t("elements.date.prevMonth")}
				todayLabel={t("elements.date.today")}
				closeLabel={t("elements.date.close")}
				disabled={isDateDisabled}
				feedback={data.feedback}
				hint={hint}
				id={eid}
				label={label}
				required={required}
				status={data.status}
				direction={isRTL() ? "rtl" : "ltr"}
				value={value.toString()}
			/>
		</>
	);
}

export default React.memo(DateReactive);
